/**
 * Calling the API.
 *
 * Every request goes to a **relative** path, which is the whole arrangement in arch §4.3: each
 * application is served by a CloudFront distribution that routes `/api/*` to the HTTP API on the
 * application's own hostname. So there is no base URL to configure, no environment variable holding
 * an API address, and nothing to keep in step with the hostname of every ephemeral stage.
 *
 * Three consequences follow, and all three are the point:
 *
 * - **No CORS anywhere.** Nothing is cross-origin, so there is no preflight round trip inside the
 *   claim and bid budgets (arch §11.1) and no origin allow-list that has to know about branch deploys.
 * - **The session cookie is sent automatically**, with the strongest attributes available —
 *   `__Host-`, `Secure`, `SameSite=Strict` — because the cookie's origin and the API's are the same.
 * - **Under `sst dev` this works through a Vite proxy** (see `vite.config.ts`), so the browser is
 *   still talking to `localhost` and the code is unchanged.
 */

export class ApiError extends Error {
  readonly status: number;
  /** The API's own `error` string, which is written to be shown to a person. */
  readonly detail: string | undefined;
  readonly body: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    const detail = typeof body.error === "string" ? body.error : undefined;
    super(detail ?? `Request failed with ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

async function request<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method,
    // `same-origin` is the default for `fetch`, and stating it is not superstition: the session
    // depends on the cookie riding along, and a later refactor that introduces an absolute URL
    // would silently stop sending it. This makes that a visible change rather than a quiet one.
    credentials: "same-origin",
    headers: body
      ? {
          // The API requires JSON on state-changing routes, as a second barrier behind
          // `SameSite=Strict` — an HTML form cannot send this content type, so the one request an
          // attacker can make a browser issue without JavaScript is refused before it reaches any
          // logic. See `packages/functions/src/lib/http.ts`.
          "content-type": "application/json",
        }
      : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Every route answers JSON, including its errors, so this is parsed before the status is judged —
  // the body is where the message worth showing lives.
  const parsed: unknown = await response.json().catch(() => ({}));
  const payload =
    parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};

  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  // M0.7. `PATCH` for a partial edit and `PUT` for the running order, which is replaced whole —
  // see `packages/functions/src/lots.ts` for why an order is sent as a list rather than as moves.
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
